import { encodeSectionsBaseUrl, errorHandler, getBrandConfig, ok } from "../../utils/util";
import createAxiosInstance from "../LambdaApiAdministracion";
import { ENVIROMENT } from "../../utilities";

const sectionsBaseUrl =  ENVIROMENT === "dev" ? "inventario/almacen" : `home/inventario/almacen`;

/**
 * Obtiene la lista de almacenes asociados a una marca específica. *
 * @param {string} brand - Identificador único de la marca (obligatorio).
 * @param {boolean} [onlyActive=true] - Indica si se deben retornar únicamente los almacenes activos.
 * @returns {Promise<Object|Array>} Datos de los almacenes correspondientes a la marca.
 * @throws {Error} Si ocurre un problema durante la solicitud o si `brand` no es proporcionado.
 */
const GetAlmacenes = async ({ brand, branchFiSucursalId, onlyActive = false } = {}) => {
  if (!brand) {
    // eslint-disable-next-line no-console
    console.warn("⚠️ El parámetro 'brand' es requerido");
    throw new Error("⚠️ El parámetro 'brand' es requerido");
  }

  if (branchFiSucursalId == null) {
    // eslint-disable-next-line no-console
    console.warn("⚠️ El parámetro 'branchFiSucursalId' es requerido3");
    throw new Error("⚠️ El parámetro 'branchFiSucursalId' es requerido3");
  }

  const axiosInstance = await createAxiosInstance();

  const url = encodeSectionsBaseUrl(`${sectionsBaseUrl}/marca`, brand);
  const requestConfig = {
    method: "GET",
    url,
    params: {
      ceco:branchFiSucursalId,
      onlyActive,
    },
    headers: {
      Accept: "application/json",
    },
  };

  try {
    const res = await axiosInstance(requestConfig);

    return ok(res.data);
  } catch (err) {
    return errorHandler(err, {
      message: "🟥 Error al obtener almacenes",
      context: { url },
    });
  }
};

/**
 * Obtiene la información de un almacén por id.
 * @function GetAlmacenById
 * @param {string|number} id - Identificador único del almacén (obligatorio).
 * @returns {Promise<Object>} Datos del almacén.
 * @throws {Error} Si ocurre un problema durante la solicitud o si `id` no es proporcionado.
 */
const GetAlmacenById = async (id) => {
  if (!id) throw new Error("Id es requerido");

  const axiosInstance = await createAxiosInstance();

  const url = encodeSectionsBaseUrl(sectionsBaseUrl, id);

  try {
    const res = await axiosInstance.get(url);
    return ok(res.data);
  } catch (err) {
    return errorHandler(err, { message: `🟥 Error al obtener almacen: ${id}` });
  }
};

/**
 * Crea un nuevo almacén.
 * @function PostAlmacen
 * @param {Object} body - Datos del almacén a crear.
 * @returns {Promise<Object>} Información del almacén creado.
 * @throws {Error} Si ocurre un problema durante la solicitud o si el cuerpo es inválido.
 */
const PostAlmacen = async (body) => {
  if (!body) {
    // eslint-disable-next-line no-console
    console.warn("⚠️ El cuerpo de la solicitud es requerido");
    throw new Error("El cuerpo de la solicitud es requerido");
  }

  const axiosInstance = await createAxiosInstance();

  try {
    const res = await axiosInstance.post(sectionsBaseUrl, body, {
      headers: { "Content-Type": "application/json" },
    });
    return ok(res.data);
  } catch (err) {
    return errorHandler(err, {
      message: "🟥 Error al crear almacén",
    });
  }
};

/**
 * Actualiza la información de un almacén existente.
 * @function PutAlmacen
 * @param {string|number} id - Identificador único del almacén que se desea actualizar (obligatorio).
 * @param {Object} body - Datos del almacén a actualizar.
 * @returns {Promise<Object>} Respuesta con la información del almacén actualizado.
 * @throws {Error} Si ocurre un problema durante la solicitud o si el `id` no es proporcionado.
 */
const PutAlmacen = async (id, body) => {
  if (!id) throw new Error("Id es requerido");

  const { brand } = body || {};
  const { WERKS, LGORT } = getBrandConfig(brand);

  const payload = {
    ...body,
    ...(WERKS && { werks: WERKS }),
    ...(LGORT && { lgort: LGORT }),
  };

  const axiosInstance = await createAxiosInstance();

  const url = encodeSectionsBaseUrl(sectionsBaseUrl, id);

  try {
    const res = await axiosInstance.put(url, payload, {
      headers: { "Content-Type": "application/json" },
    });

    return ok(res.data);
  } catch (err) {
    return errorHandler(err, {
      message: `🟥 Error al actualizar almacen: ${id}`,
    });
  }
};

/**
 * Actualiza el status de un almacén existente.
 * @function PutAlmacen
 * @param {string|number} id - Identificador único del almacén que se desea actualizar (obligatorio).
 * @param {Object} body - Datos del almacén a actualizar.
 * @returns {Promise<Object>} Respuesta con la información del almacén actualizado.
 * @throws {Error} Si ocurre un problema durante la solicitud o si el `id` no es proporcionado.
 */
const PatchAlmacen = async (id, { newStatus: active, brand }) => {
  if (!id) throw new Error("Id es requerido");

  if (active === undefined || active === null) {
    throw new Error("El parámetro 'active' es requerido y debe ser booleano.");
  }

  const axiosInstance = await createAxiosInstance();
  const { LGORT } = getBrandConfig(brand);

  const url = encodeSectionsBaseUrl(sectionsBaseUrl, id);
  const mappedurl = `${url}/status`;

  try {
    const res = await axiosInstance.patch(mappedurl, active, {
      headers: {
        "Content-Type": "application/json",
        ...(LGORT && { lgort: LGORT }),
      },
    });

    return ok(res.data);
  } catch (err) {
    return errorHandler(err, {
      message: `🟥 Error al actualizar status del almacen: ${id}`,
    });
  }
};

/**
 * Elimina un almacén existente dado su identificador único.
 * @function DeleteAlmacen
 * @param {string|number} id - Identificador único del almacén que se desea eliminar (obligatorio).
 * @returns {Promise<Object>} Respuesta del backend tras eliminar el almacén.
 * @throws {Error} Si ocurre un problema durante la solicitud o si el `id` no es proporcionado.
 */
const DeleteAlmacen = async (id) => {
  if (!id) throw new Error("id requerido");

  const axiosInstance = await createAxiosInstance();

  const url = encodeSectionsBaseUrl(sectionsBaseUrl, id);

  try {
    const res = await axiosInstance.delete(url);
    return ok(res.data);
  } catch (err) {
    return errorHandler(err, {
      message: `🟥 Error al eliminar almacen: ${id}`,
    });
  }
};

export const sectionsService = {
  getAll: GetAlmacenes,
  getById: GetAlmacenById,
  create: PostAlmacen,
  update: PutAlmacen,
  updateStatus: PatchAlmacen,
  delete: DeleteAlmacen,
};
